import React, { type FC } from "react";
import classNames from "classnames";
import { formatFullName } from "@src/helpers/text-services/formatFullName";

/** Минимальный контракт ФИО из Autovaz `Labouring` для отображения. */
export interface BusinessUserValueLabouring {
    /** Фамилия. */
    SurName?: string | null;
    /** Имя. */
    Name?: string | null;
    /** Отчество. */
    Patronymic?: string | null;
}

/** Минимальный контракт пользователя для отображения. */
export interface BusinessUserValueUser {
    /**
     * Полное имя пользователя. \
     * Основной текст, если заполнено (`user.loadListByFilter` / `ApiUserListItem.FullName`).
     */
    FullName?: string | null;
    /**
     * Логин универсального пользователя OMP (`LoginName`). \
     * Фолбэк, если нет `FullName` и ФИО из `Labouring`.
     */
    LoginName?: string | null;
    /**
     * Логин Autovaz-пользователя (`Login`). \
     * Фолбэк после `LoginName`.
     */
    Login?: string | null;
    /**
     * Кадровые данные Autovaz-пользователя. \
     * Фолбэк ФИО, если `FullName` пуст.
     */
    Labouring?: BusinessUserValueLabouring | null;
}

/** Пропсы компонента {@link BusinessUserValue}. */
export interface BusinessUserValueProps {
    /**
     * Пользователь. \
     * Основной текст: `FullName`; иначе ФИО из `Labouring`; иначе `(логин: LoginName|Login)`;
     * иначе «Безымянный пользователь».
     */
    user?: BusinessUserValueUser | null;
    /** Текст или узел, если объекта пользователя нет. @default "Не задано" */
    fallback?: React.ReactNode;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
}

/** Подпись, когда объект пользователя есть, но отображаемые поля пусты. */
const UNNAMED_USER_LABEL = "«Безымянный пользователь»";

function resolveTextPart(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text === "" ? null : (text ?? null);
}

/**
 * ФИО из `Labouring` в формате проекта `Sf N. P.`.
 * @param labouring кадровые данные пользователя
 * @returns строка ФИО или `null`, если все части пусты
 */
function resolveLabouringText(labouring?: BusinessUserValueLabouring | null): string | null {
    if (labouring == null) {
        return null;
    }

    const surName = resolveTextPart(labouring.SurName);
    const name = resolveTextPart(labouring.Name);
    const patronymic = resolveTextPart(labouring.Patronymic);

    if (surName == null && name == null && patronymic == null) {
        return null;
    }

    return resolveTextPart(formatFullName(surName ?? "", name ?? "", patronymic ?? ""));
}

/**
 * Основной текст пользователя по приоритету полей.
 * @param user пользователь
 * @returns `FullName` → ФИО из `Labouring` → `(логин: …)` → «Безымянный пользователь»; `null`, если объекта нет
 */
function resolveUserPrimaryText(user?: BusinessUserValueUser | null): string | null {
    if (user == null) {
        return null;
    }

    const fullName = resolveTextPart(user.FullName);
    if (fullName != null) {
        return fullName;
    }

    const labouringText = resolveLabouringText(user.Labouring);
    if (labouringText != null) {
        return labouringText;
    }

    const login = resolveTextPart(user.LoginName) ?? resolveTextPart(user.Login);
    if (login != null) {
        return `(логин: ${login})`;
    }

    return UNNAMED_USER_LABEL;
}

/**
 * Формирует plain-text представление пользователя.
 * @param user пользователь (`FullName` → ФИО из `Labouring` → `(логин: …)` → «Безымянный пользователь»)
 * @returns строка для отображения или пустая, если объекта нет
 */
export function formatUserText(user?: BusinessUserValueUser | null): string {
    return resolveUserPrimaryText(user) ?? "";
}

/**
 * Read-only отображение пользователя. \
 * `FullName` — основной текст; при его отсутствии — ФИО из `Labouring`, затем помеченный логин.
 */
export const BusinessUserValue: FC<BusinessUserValueProps> = ({ user, fallback = "Не задано", className }) => {
    const primary = resolveUserPrimaryText(user);

    if (primary == null) {
        return <span className={className}>{fallback}</span>;
    }

    return (
        <span className={classNames("business_user_value", className)}>
            <span className="business_user_value__full_name">{primary}</span>
        </span>
    );
};
